import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import ConfirmButton from "./../../Shared/Buttons/ConfirmButton/index";
import CancelButton from "../../Shared/Buttons/CancelButton";
import InlineAlert from "../../Shared/InlineAlert";
import { ReactComponent as SpinnerIcon } from "../../../assets/img/icons/spinner.svg";
import { useUserApi } from "../../../Hooks";
import { emailRegex } from "../../../Configs";
import TextInput from "../../Shared/TextInput";
import { useTranslation } from "react-i18next";

export default function ForgotPasswordModal({ isOpen, onClose, language, isLegacyTemplate }) {
  const { t, i18n } = useTranslation();
  const [alert, setAlert] = useState({});
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const userApi = useUserApi();

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  
  useEffect(() => {
    setEmail("");
    setAlert({});
  }, [isOpen]);

  const handleForgotPassword = async () => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 500));

    if (!emailRegex.test(email)) {
      setAlert({ type: "error", message: t("forgotPasswordInvalidEmail") });
    } else {
      const forgotPasswordResult = await userApi.forgotPassword(email, isLegacyTemplate ?? true);
      if (forgotPasswordResult.status === 200) {
        setAlert({ type: "success", message: t("forgotPasswordSuccess") });
        setLoading(false);
        await new Promise((r) => setTimeout(r, 1000));
        onClose();
      } else {
        setAlert({ type: "error", message: t("forgotPasswordError") });
      }
    }
    setLoading(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full items-center justify-center p-4 text-center ${
              loading && "cursor-wait"
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col max-w-md h-86 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg leading-6 font-dosisbold">
                  {t("forgotPasswordModalTitle")}
                </Dialog.Title>
                <div className="flex flex-col px-6 py-6">
                  <div className="text-center text-md rounded-lg mb-2 font-bold py-1 transition-opacity duration-200 ease-in-out">
                    {t("forgotPasswordModalDescription")}
                  </div>

                  <InlineAlert
                    alert={alert}
                    visible={alert.type === "error" || alert.type === "success"}
                  />
                  {loading ? (
                    <SpinnerIcon className="m-auto w-16 h-16" />
                  ) : (
                    <TextInput
                      id="forgotPasswordEmail"
                      label={t("forgotPasswordEmailLabel")}
                      placeholder={t("forgotPasswordEmailPlaceholder")}
                      type="text"
                      error={alert.type === "error"}
                      value={email}
                      onChangeText={(e) => setEmail(e.target.value)}
                      className="mt-2"
                      size="sm"
                    />
                  )}

                  <div className="mt-5 ml-auto flex flex-row gap-x-3">
                    <CancelButton onClick={onClose} />
                    <ConfirmButton onClick={handleForgotPassword} />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
